import React, { useState } from 'react';
import { aliasFromEmail, useAuth } from '../auth';
import { UnscheduledEvent } from '../types';
import {
  TriageItem,
  dismissTriageItem,
  dismissTriageItemsInBatches,
} from '../services/triageService';
import type { TriageErtsAction } from '../services/triageService';
import { createLogger } from '../utils/logger';
import {
  PreparedTriageEvent,
  prepareTriageEvent,
  suppressSourceNote,
} from './triage-event-builder';
import TriageReviewModal, { TriageDecision } from './TriageReviewModal';

const logger = createLogger('TriageSection');

/** A reviewed Triage item staged for completion in the normal Event modal. */
export interface TriageEventDraft {
  item: TriageItem;
  action: 'CREATE' | 'UPDATE';
  event: UnscheduledEvent;
  ertsAction: TriageErtsAction;
  ertsExistedBefore: boolean;
  /** Defects added by this review that require a Knowledge Base ATA recommendation. */
  recommendationDefectIds: number[];
}

interface TriageSectionProps {
  items: TriageItem[];
  isMock: boolean;
  loading: boolean;
  /** When false, review/dismiss actions are disabled (read-only users). */
  canEdit: boolean;
  /** Restricts the destructive bulk action to beta and gamma environments. */
  allowClearAll: boolean;
  /** Opens the normal Create/Edit Event modal with the reviewed fields staged. */
  onOpenEvent: (draft: TriageEventDraft) => void;
  /** Removes a successfully handled item from the global pending list. */
  onRemoveItem: (triageId: string) => void;
}

/**
 * Renders a triage item's ingest time (`receivedAt`, the extract row's
 * `ingested_at`, stored as UTC/Zulu) as stacked date over Zulu time.
 */
function formatNotificationReceived(receivedAt: string): React.ReactNode {
  if (!receivedAt) return '—';
  const [datePart, timePart] = receivedAt.trim().split(/[T ]/);
  const date: string = datePart || '';
  const time: string = timePart ? timePart.substring(0, 5) : '';
  return (
    <div className="datetime-stacked">
      <span className="datetime-date">{date}</span>
      {time && <span className="datetime-time">{time}z</span>}
    </div>
  );
}

/** Orders triage items by ingest time, oldest first. */
function byReceivedOldestFirst(a: TriageItem, b: TriageItem): number {
  return String(a.receivedAt).localeCompare(String(b.receivedAt));
}

/**
 * Triage section: lists pending email-triage items and lets an edit-access
 * reviewer create or update an event from each, or dismiss it. Renders mock
 * data with a banner when the triage API isn't reachable (local preview).
 */
const TriageSection: React.FC<TriageSectionProps> = ({
  items,
  isMock,
  loading,
  canEdit,
  allowClearAll,
  onOpenEvent,
  onRemoveItem,
}) => {
  const { user } = useAuth();
  const currentUserAlias: string = aliasFromEmail(user?.email);
  const [selected, setSelected] = useState<TriageItem | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  /**
   * Prepares the reviewed values, then opens the normal Event modal. The actual
   * triage commit happens only after the user completes required fields and
   * selects Create/Update in that modal.
   */
  const handleCommit = async (submittedDecision: TriageDecision): Promise<void> => {
    if (!selected) return;
    const decision: TriageDecision = suppressSourceNote(selected, submittedDecision);
    setBusy(true);
    try {
      const prepared: PreparedTriageEvent | null = await prepareTriageEvent(
        selected,
        decision,
        currentUserAlias,
      );
      if (!prepared) return;
      onOpenEvent({ item: selected, action: decision.action, ...prepared });
      setSelected(null);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async (): Promise<void> => {
    if (!selected) return;
    setBusy(true);
    try {
      const ok = await dismissTriageItem(selected.triage_id, selected.version);
      if (ok || isMock) {
        onRemoveItem(selected.triage_id);
        setSelected(null);
      }
    } finally {
      setBusy(false);
    }
  };

  /** Dismisses every currently open triage item, retaining any failed rows. */
  const handleClearAll = async (): Promise<void> => {
    if (!allowClearAll || !canEdit || busy || items.length === 0) return;
    const confirmed: boolean = window.confirm(`Dismiss all ${items.length} open triage events?`);
    if (!confirmed) return;

    setBusy(true);
    try {
      const dismissedIds: Set<string> = isMock
        ? new Set<string>(items.map((item: TriageItem): string => item.triage_id))
        : await dismissTriageItemsInBatches(items);
      dismissedIds.forEach((triageId: string): void => onRemoveItem(triageId));
      setSelected((current: TriageItem | null): TriageItem | null =>
        current && dismissedIds.has(current.triage_id) ? null : current,
      );
      const failedCount: number = items.length - dismissedIds.size;
      logger.info('Triage Clear All completed', {
        dismissedCount: dismissedIds.size,
        failedCount,
      });
      if (failedCount > 0) {
        window.alert(`${failedCount} triage events could not be dismissed. Please retry.`);
      }
    } catch (error: unknown) {
      logger.error('Triage Clear All failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      window.alert('Triage events could not be dismissed. Please retry.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="triage-section">
        <p className="triage-empty">Loading triage…</p>
      </div>
    );
  }

  return (
    <div className="triage-section">
      <div className="triage-header">
        <h2 className="triage-title">Triage — pending email updates</h2>
        {allowClearAll && (
          <button
            className="modal-btn modal-btn-danger clear-all-btn"
            type="button"
            disabled={!canEdit || busy || items.length === 0}
            onClick={handleClearAll}
          >
            {busy ? 'Clearing…' : 'Clear All'}
          </button>
        )}
        {isMock && (
          <span
            className="triage-mock-badge"
            title="The triage API is not reachable; showing preview data"
          >
            Preview data (API not connected)
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <p className="triage-empty">No pending triage items.</p>
      ) : (
        <table className="triage-table">
          <thead>
            <tr>
              <th>Notification Received</th>
              <th>Tail</th>
              <th>Carrier</th>
              <th>Type</th>
              <th>Suggested</th>
              <th>Source</th>
              <th>Subject</th>
              <th>Flags</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {[...items].sort(byReceivedOldestFirst).map((item) => (
              <tr
                key={item.triage_id}
                className={item.parseStatus !== 'OK' ? 'triage-row-warn' : ''}
              >
                <td className="triage-received">{formatNotificationReceived(item.receivedAt)}</td>
                <td>{item.tailNumber || '—'}</td>
                <td>{item.carrier || '—'}</td>
                <td>{item.targetRecordType || '—'}</td>
                <td>{item.suggestedAction || '—'}</td>
                <td>{item.sourceProfileId || item.sourceEmail}</td>
                <td className="triage-subject">{item.subject}</td>
                <td>
                  {!item.senderVerified && (
                    <span className="triage-flag" title="Sender unverified">
                      ⚠
                    </span>
                  )}
                  {!item.tailInFleet && (
                    <span className="triage-flag" title="Tail not in fleet">
                      ⚑
                    </span>
                  )}
                  {item.parseWarnings.length > 0 && (
                    <span className="triage-flag" title={item.parseWarnings.join('; ')}>
                      {item.parseWarnings.length}
                    </span>
                  )}
                </td>
                <td>
                  <button className="modal-btn" type="button" onClick={() => setSelected(item)}>
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <TriageReviewModal
          item={selected}
          canEdit={canEdit}
          busy={busy}
          onCommit={handleCommit}
          onDismiss={handleDismiss}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default TriageSection;
